import { sb } from '../supabase.ts';
import { escapeHtml } from '../main.ts';

async function sha256(message: ArrayBuffer | string): Promise<string> {
  const msgBuffer = typeof message === 'string' ? new TextEncoder().encode(message) : message;
  const hashBuffer = await crypto.subtle.digest('SHA-256', msgBuffer);
  const hashArray = Array.from(new Uint8Array(hashBuffer));
  return hashArray.map(b => b.toString(16).padStart(2, '0')).join('');
}

async function hmacSha256(key: ArrayBuffer | string, data: string | Uint8Array): Promise<ArrayBuffer> {
  const encoder = new TextEncoder();
  const keyData = typeof key === 'string' ? encoder.encode(key) : key;
  const dataBytes = typeof data === 'string' ? encoder.encode(data) : data;
  const cryptoKey = await crypto.subtle.importKey(
    'raw',
    keyData,
    { name: 'HMAC', hash: 'SHA-256' },
    false,
    ['sign']
  );
  return await crypto.subtle.sign('HMAC', cryptoKey, dataBytes as any);
}

async function getSignatureKey(key: string, dateStamp: string, regionName: string, serviceName: string): Promise<ArrayBuffer> {
  const kDate = await hmacSha256("AWS4" + key, dateStamp);
  const kRegion = await hmacSha256(kDate, regionName);
  const kService = await hmacSha256(kRegion, serviceName);
  return await hmacSha256(kService, "aws4_request");
}

async function sign(key: ArrayBuffer, msg: string): Promise<string> {
  const signatureBuffer = await hmacSha256(key, msg);
  const signatureArray = Array.from(new Uint8Array(signatureBuffer));
  return signatureArray.map(b => b.toString(16).padStart(2, '0')).join('');
}

function base64ToArrayBuffer(base64: string): ArrayBuffer {
  const binaryString = window.atob(base64);
  const len = binaryString.length;
  const bytes = new Uint8Array(len);
  for (let i = 0; i < len; i++) {
    bytes[i] = binaryString.charCodeAt(i);
  }
  return bytes.buffer;
}

export async function renderImageBackupPage(app: HTMLElement): Promise<void> {
  app.innerHTML = `
    <style>
      .ib-wrap {
        background: #f8fafc;
        min-height: 100vh;
        font-family: 'Outfit', 'Segoe UI', sans-serif;
        color: #1e293b;
        padding-bottom: 60px;
      }
      .ib-header {
        background: linear-gradient(135deg, #0f172a, #1e293b);
        color: #fff;
        padding: 16px 24px;
        display: flex;
        align-items: center;
        gap: 16px;
        box-shadow: 0 4px 20px rgba(0,0,0,0.15);
      }
      .ib-header h1 { margin: 0; font-size: 20px; font-weight: 800; }
      .ib-header a { color: #94a3b8; text-decoration: none; font-size: 22px; transition: color 0.2s; }
      .ib-header a:hover { color: #fff; }
      .ib-container { padding: 24px; max-width: 1100px; margin: 0 auto; }
      .ib-stats {
        display: flex;
        gap: 16px;
        margin-bottom: 24px;
        flex-wrap: wrap;
      }
      .stat-card {
        background: #fff;
        border: 1px solid rgba(0,0,0,0.08);
        border-radius: 12px;
        padding: 16px 24px;
        flex: 1;
        min-width: 140px;
        box-shadow: 0 2px 8px rgba(0,0,0,0.04);
      }
      .stat-card .num { font-size: 28px; font-weight: 800; }
      .stat-card .lbl { font-size: 12px; color: #64748b; font-weight: 600; text-transform: uppercase; letter-spacing: 0.5px; margin-top: 2px; }
      .stat-card.done .num { color: #10b981; }
      .stat-card.pending .num { color: #f59e0b; }
      .stat-card.total .num { color: #0ea5e9; }
      .toolbar {
        display: flex;
        gap: 12px;
        margin-bottom: 16px;
        align-items: center;
        flex-wrap: wrap;
      }
      .btn-backup-all {
        background: linear-gradient(135deg, #10b981, #059669);
        color: #fff;
        border: none;
        padding: 10px 20px;
        border-radius: 8px;
        font-size: 14px;
        font-weight: 700;
        cursor: pointer;
        transition: all 0.2s;
      }
      .btn-backup-all:hover { transform: translateY(-1px); box-shadow: 0 4px 12px rgba(16,185,129,0.3); }
      .btn-backup-all:disabled { opacity: 0.5; cursor: not-allowed; transform: none; }
      #globalLog {
        font-size: 12px;
        color: #64748b;
        margin-left: 8px;
      }
      .table-wrap {
        background: #fff;
        border: 1px solid rgba(0,0,0,0.08);
        border-radius: 12px;
        overflow: hidden;
        box-shadow: 0 4px 16px rgba(0,0,0,0.04);
      }
      table { width: 100%; border-collapse: collapse; font-size: 13.5px; }
      th {
        background: #f8fafc;
        padding: 12px 14px;
        text-align: left;
        font-size: 11px;
        font-weight: 700;
        text-transform: uppercase;
        letter-spacing: 0.5px;
        color: #64748b;
        border-bottom: 1px solid rgba(0,0,0,0.08);
      }
      td {
        padding: 10px 14px;
        border-bottom: 1px solid rgba(0,0,0,0.04);
        vertical-align: middle;
      }
      tbody tr:hover { background: #f8fafc; }
      .thumb {
        width: 40px;
        height: 48px;
        object-fit: cover;
        border-radius: 6px;
        border: 1px solid rgba(0,0,0,0.08);
      }
      .status-done {
        color: #10b981;
        font-weight: 700;
        font-size: 12px;
        background: rgba(16,185,129,0.1);
        padding: 4px 10px;
        border-radius: 20px;
        display: inline-block;
      }
      .status-no-photo {
        color: #94a3b8;
        font-size: 12px;
      }
      .btn-backup {
        background: linear-gradient(135deg, #0ea5e9, #0284c7);
        color: #fff;
        border: none;
        padding: 6px 14px;
        border-radius: 6px;
        font-size: 12px;
        font-weight: 700;
        cursor: pointer;
        transition: all 0.2s;
        white-space: nowrap;
      }
      .btn-backup:hover { transform: translateY(-1px); box-shadow: 0 4px 10px rgba(14,165,233,0.3); }
      .btn-backup:disabled { opacity: 0.5; cursor: not-allowed; transform: none; }
      .backup-url-cell {
        font-size: 11px;
        color: #64748b;
        word-break: break-all;
        max-width: 200px;
      }
      .backup-url-cell a { color: #0ea5e9; }
    </style>

    <div class="ib-wrap">
      <header class="ib-header">
        <a href="/">⬅</a>
        <h1>🖼️ Photo Backup — Cloudflare R2</h1>
        <span id="loadStatus" style="margin-left:auto; font-size:13px; color:#94a3b8;">Loading...</span>
      </header>

      <div class="ib-container">
        <div class="ib-stats">
          <div class="stat-card total"><div class="num" id="statTotal">—</div><div class="lbl">Total Students (With Photo)</div></div>
          <div class="stat-card done"><div class="num" id="statDone">—</div><div class="lbl">Backed Up ✅</div></div>
          <div class="stat-card pending"><div class="num" id="statPending">—</div><div class="lbl">Pending 🔄</div></div>
        </div>

        <div style="display: flex; gap: 8px; margin-bottom: 20px; border-bottom: 2px solid #e2e8f0; padding-bottom: 8px;">
          <button id="tabPendingBtn" style="padding: 10px 20px; border: none; background: #0ea5e9; color: white; border-radius: 8px; font-weight: 700; cursor: pointer; transition: all 0.2s;">Pending Backup (<span id="tabPendingCount">0</span>)</button>
          <button id="tabUploadedBtn" style="padding: 10px 20px; border: none; background: transparent; color: #64748b; border-radius: 8px; font-weight: 700; cursor: pointer; transition: all 0.2s;">Uploaded Section (<span id="tabUploadedCount">0</span>)</button>
        </div>

        <div class="toolbar" id="toolbarPending">
          <button class="btn-backup-all" id="backupAllBtn">⚡ Backup All Pending</button>
          <span id="globalLog"></span>
        </div>

        <div class="table-wrap">
          <table>
            <thead>
              <tr>
                <th>#</th>
                <th>IID</th>
                <th>Student Photo</th>
                <th>Backup URL</th>
                <th>Status / Action</th>
              </tr>
            </thead>
            <tbody id="tableBody">
              <tr><td colspan="5" style="text-align:center; padding:40px; color:#94a3b8;">Loading data...</td></tr>
            </tbody>
          </table>
        </div>
      </div>
    </div>
  `;

  const tbody = document.getElementById('tableBody')!;
  const loadStatus = document.getElementById('loadStatus')!;
  const globalLog = document.getElementById('globalLog')!;
  const backupAllBtn = document.getElementById('backupAllBtn') as HTMLButtonElement;
  const statTotal = document.getElementById('statTotal')!;
  const statDone = document.getElementById('statDone')!;
  const statPending = document.getElementById('statPending')!;
  
  const tabPendingBtn = document.getElementById('tabPendingBtn') as HTMLButtonElement;
  const tabUploadedBtn = document.getElementById('tabUploadedBtn') as HTMLButtonElement;
  const tabPendingCount = document.getElementById('tabPendingCount')!;
  const tabUploadedCount = document.getElementById('tabUploadedCount')!;
  const toolbarPending = document.getElementById('toolbarPending')!;

  let allRows: any[] = [];
  let currentTab: 'pending' | 'uploaded' = 'pending';

  // Tab click events
  tabPendingBtn.onclick = () => {
    currentTab = 'pending';
    tabPendingBtn.style.background = '#0ea5e9';
    tabPendingBtn.style.color = 'white';
    tabUploadedBtn.style.background = 'transparent';
    tabUploadedBtn.style.color = '#64748b';
    toolbarPending.style.display = 'flex';
    renderTable();
  };

  tabUploadedBtn.onclick = () => {
    currentTab = 'uploaded';
    tabUploadedBtn.style.background = '#10b981';
    tabUploadedBtn.style.color = 'white';
    tabPendingBtn.style.background = 'transparent';
    tabPendingBtn.style.color = '#64748b';
    toolbarPending.style.display = 'none';
    renderTable();
  };

  // ── Load data ────────────────────────────────────────────────────────
  async function loadData() {
    loadStatus.textContent = 'Loading...';
    try {
      const batchSize = 1000;
      let lastId = 0;
      let all: any[] = [];

      while (true) {
        const { data, error } = await sb
          .from('student_database')
          .select('iid, student_photo_url, image_backup_url')
          .gt('iid', lastId)
          .order('iid', { ascending: true })
          .limit(batchSize);

        if (error) throw error;
        if (!data || data.length === 0) break;
        all = all.concat(data);
        lastId = data[data.length - 1].iid;
        if (data.length < batchSize) break;
      }

      // ONLY show students who have a valid student_photo_url
      allRows = all.filter(r => r.student_photo_url && String(r.student_photo_url).trim() !== '' && String(r.student_photo_url) !== 'null');
      
      renderTable();
      updateStats();
      loadStatus.textContent = `Loaded ${allRows.length} records`;
    } catch (e: any) {
      loadStatus.textContent = 'Error loading data';
      tbody.innerHTML = `<tr><td colspan="5" style="color:#ef4444; text-align:center; padding:30px;">${escapeHtml(e.message)}</td></tr>`;
    }
  }

  function updateStats() {
    const total   = allRows.length;
    const done    = allRows.filter(r => r.image_backup_url).length;
    const pending = allRows.filter(r => !r.image_backup_url).length;
    statTotal.textContent   = String(total);
    statDone.textContent    = String(done);
    statPending.textContent = String(pending);
    
    tabPendingCount.textContent = String(pending);
    tabUploadedCount.textContent = String(done);
  }

  // ── Render table ─────────────────────────────────────────────────────
  function renderTable() {
    tbody.innerHTML = '';
    
    // Filter rows based on selected tab
    const filteredRows = allRows.filter(r => {
      if (currentTab === 'pending') return !r.image_backup_url;
      if (currentTab === 'uploaded') return !!r.image_backup_url;
      return true;
    });

    if (filteredRows.length === 0) {
      tbody.innerHTML = `<tr><td colspan="5" style="text-align:center; padding:40px; color:#94a3b8;">No records found in this section.</td></tr>`;
      return;
    }

    filteredRows.forEach((row, idx) => {
      const tr = document.createElement('tr');
      tr.dataset.iid = String(row.iid);

      const hasPhoto  = !!row.student_photo_url;
      const hasBacked = !!row.image_backup_url;

      let actionCell = '';
      if (hasBacked) {
        actionCell = `<span class="status-done">✅ Done</span>`;
      } else {
        actionCell = `<button class="btn-backup" data-iid="${row.iid}" data-photo="${escapeHtml(row.student_photo_url)}">Backup</button>`;
      }

      tr.innerHTML = `
        <td>${idx + 1}</td>
        <td style="font-weight:700; color:#0f172a;">${escapeHtml(String(row.iid))}</td>
        <td>
          ${hasPhoto
            ? `<img class="thumb" src="${escapeHtml(row.student_photo_url)}" onerror="this.style.display='none'">`
            : `<span style="color:#94a3b8; font-size:11px;">—</span>`}
        </td>
        <td class="backup-url-cell">
          ${hasBacked 
            ? `<div style="display:flex; align-items:center; gap:8px;">
                 <img class="thumb" src="${escapeHtml(row.image_backup_url)}" onerror="this.style.display='none';">
                 <a href="${escapeHtml(row.image_backup_url)}" target="_blank" style="font-size:12px; color:#2563eb; text-decoration:none; font-weight:600;">View ↗</a>
               </div>` 
            : '—'}
        </td>
        <td id="action-${row.iid}">${actionCell}</td>
      `;

      tbody.appendChild(tr);
    });

    // Attach backup button events
    tbody.querySelectorAll('.btn-backup').forEach((btn: any) => {
      btn.onclick = () => backupOne(btn.dataset.iid, btn.dataset.photo, btn);
    });
  }

  // ── Compress image to under 80KB using canvas ────────────────────────
  async function compressImage(arrayBuffer: ArrayBuffer, mimeType: string): Promise<{ base64: string; mimeType: string }> {
    return new Promise((resolve, reject) => {
      const blob = new Blob([arrayBuffer], { type: mimeType });
      const objectUrl = URL.createObjectURL(blob);
      const img = new Image();
      const timeoutId = setTimeout(() => {
        img.onload = null;
        img.onerror = null;
        URL.revokeObjectURL(objectUrl);
        reject(new Error('Image compression/loading timed out (10s limit)'));
      }, 10000);

      img.onload = () => {
        clearTimeout(timeoutId);
        URL.revokeObjectURL(objectUrl);
        try {
          const MAX_BYTES = 80 * 1024; // 80KB
          const canvas = document.createElement('canvas');
          const ctx = canvas.getContext('2d')!;

          let w = img.naturalWidth;
          let h = img.naturalHeight;
          let quality = 0.95;
          let scale = 1.0;

          const tryCompress = () => {
            try {
              canvas.width = Math.floor(img.naturalWidth * scale);
              canvas.height = Math.floor(img.naturalHeight * scale);
              ctx.clearRect(0, 0, canvas.width, canvas.height);
              ctx.drawImage(img, 0, 0, canvas.width, canvas.height);

              const dataUrl = canvas.toDataURL('image/jpeg', quality);
              const base64 = dataUrl.split(',')[1];
              const bytes = Math.ceil(base64.length * 3 / 4);

              if (bytes <= MAX_BYTES) {
                resolve({ base64, mimeType: 'image/jpeg' });
              } else {
                // Try reducing quality first in small steps
                if (quality > 0.2) {
                  quality -= 0.05;
                  tryCompress();
                } else if (scale > 0.1) {
                  // If quality is already low, reduce dimensions slightly (e.g. 5%) and reset quality to 0.7
                  scale -= 0.05;
                  quality = 0.7;
                  tryCompress();
                } else {
                  // Fallback: resolve with what we have
                  resolve({ base64, mimeType: 'image/jpeg' });
                }
              }
            } catch (err) {
              reject(new Error('Canvas/CORS error: ' + (err as Error).message));
            }
          };

          tryCompress();
        } catch (err) {
          reject(new Error('Image processing error: ' + (err as Error).message));
        }
      };
      img.onerror = () => {
        clearTimeout(timeoutId);
        URL.revokeObjectURL(objectUrl);
        reject(new Error('Image failed to load (CORS or broken URL)'));
      };
      img.src = objectUrl;
    });
  }

  // ── S3 Upload Helper using AWS Signature V4 ─────────────────────────
  async function uploadToR2Direct(iid: string, data: ArrayBuffer | string, mimeType: string): Promise<string> {
    const accessKeyId = 'd04b8b440825d4bf9d24e1c73ddb7f6a';
    const secretKey   = '0a50dbcf929476fdf21c0003f1362dd554c4f581ed46a77aa976fff2da655c29';
    const accountId   = 'e250e6d5d37a312c81e7aefa112c5f73';
    const bucket      = 'model';
    const region      = 'auto';
    const host        = `${accountId}.r2.cloudflarestorage.com`;
    const service     = 's3';

    const ext = mimeType === 'image/png' ? 'png' : 'jpg';
    const objectKey = `photo/avater-${iid}.${ext}`;
    const method = 'PUT';
    const requestUri = `/${bucket}/${objectKey}`;

    const arrayBuffer = typeof data === 'string' ? base64ToArrayBuffer(data) : data;
    const date = new Date();
    const amzDate = date.toISOString().replace(/[:-]/g, '').split('.')[0] + 'Z';
    const dateStamp = amzDate.substring(0, 8);

    const payloadHash = await sha256(arrayBuffer);

    const headers: Record<string, string> = {
      'content-type': mimeType,
      'host': host,
      'x-amz-content-sha256': payloadHash,
      'x-amz-date': amzDate
    };

    const canonicalHeaders = Object.keys(headers)
      .sort()
      .map(key => `${key}:${headers[key].trim()}\n`)
      .join('');

    const signedHeaders = Object.keys(headers)
      .sort()
      .join(';');

    const canonicalQuery = '';

    const canonicalRequest = [
      method,
      requestUri,
      canonicalQuery,
      canonicalHeaders,
      signedHeaders,
      payloadHash
    ].join('\n');

    const credentialScope = `${dateStamp}/${region}/${service}/aws4_request`;
    const hashedCanonicalRequest = await sha256(canonicalRequest);

    const stringToSign = [
      'AWS4-HMAC-SHA256',
      amzDate,
      credentialScope,
      hashedCanonicalRequest
    ].join('\n');

    const signingKey = await getSignatureKey(secretKey, dateStamp, region, service);
    const signature = await sign(signingKey, stringToSign);

    const authorizationHeader = `AWS4-HMAC-SHA256 Credential=${accessKeyId}/${credentialScope}, SignedHeaders=${signedHeaders}, Signature=${signature}`;

    const url = `https://${host}${requestUri}`;
    const response = await fetch(url, {
      method: method,
      headers: {
        'Authorization': authorizationHeader,
        'Content-Type': mimeType,
        'x-amz-content-sha256': payloadHash,
        'x-amz-date': amzDate
      },
      body: arrayBuffer
    });

    if (!response.ok) {
      const errText = await response.text();
      throw new Error(`R2 direct upload failed (${response.status}): ${errText}`);
    }

    return `https://pub-738cf145a4f6477b95ea0e09c425a15d.r2.dev/${objectKey}`;
  }

  // ── Helper to download image as ArrayBuffer for size verification ───
  async function downloadImageAsArrayBuffer(url: string): Promise<{ arrayBuffer: ArrayBuffer; mimeType: string }> {
    const controller = new AbortController();
    const id = setTimeout(() => controller.abort(), 8000); // 8 second timeout
    try {
      const res = await fetch(url, { signal: controller.signal });
      clearTimeout(id);
      if (!res.ok) throw new Error(`Failed to fetch image from source URL (HTTP ${res.status})`);
      const mimeType = res.headers.get('content-type') || 'image/jpeg';
      const arrayBuffer = await res.arrayBuffer();
      return { arrayBuffer, mimeType };
    } catch (err: any) {
      clearTimeout(id);
      if (err.name === 'AbortError') {
        throw new Error('Image download timed out (8s limit)');
      }
      throw err;
    }
  }

  // ── Backup single student ────────────────────────────────────────────
  async function backupOne(iid: string, photoUrl: string, btn?: HTMLElement) {
    const actionCell = document.getElementById(`action-${iid}`);
    if (actionCell) actionCell.innerHTML = `<span style="color:#f59e0b; font-size:12px;">⏳ Processing...</span>`;
    if (btn) (btn as HTMLButtonElement).disabled = true;

    try {
      // 1. Download image to verify size
      const { arrayBuffer, mimeType } = await downloadImageAsArrayBuffer(photoUrl);
      const originalBytes = arrayBuffer.byteLength;
      const MAX_BYTES = 80 * 1024; // 80KB

      let finalData: ArrayBuffer | string = arrayBuffer;
      let finalMime = mimeType;

      if (originalBytes > MAX_BYTES) {
        console.log(`IID ${iid}: Image size is ${Math.round(originalBytes / 1024)}KB (>80KB). Compressing client-side...`);
        // Compress client-side to base64
        const { base64, mimeType: compressedMime } = await compressImage(arrayBuffer, mimeType);
        finalData = base64;
        finalMime = compressedMime;
      } else {
        console.log(`IID ${iid}: Image size is ${Math.round(originalBytes / 1024)}KB (<=80KB). Uploading directly without compression...`);
      }

      // 2. Upload directly to Cloudflare R2 S3 Endpoint
      const backupUrl = await uploadToR2Direct(iid, finalData, finalMime);

      // 3. Save URL to Supabase
      const { error: dbErr } = await sb
        .from('student_database')
        .update({ image_backup_url: backupUrl })
        .eq('iid', Number(iid));

      if (dbErr) throw new Error('Supabase update failed: ' + dbErr.message);

      // 4. Update local cache
      const rowIdx = allRows.findIndex(r => String(r.iid) === String(iid));
      if (rowIdx !== -1) allRows[rowIdx].image_backup_url = backupUrl;

      // 5. Update UI
      if (actionCell) {
        actionCell.innerHTML = `<span class="status-done">✅ Done</span>`;
        const tr = actionCell.closest('tr');
        if (tr) {
          const backupUrlCell = tr.querySelector('.backup-url-cell');
          if (backupUrlCell) {
            backupUrlCell.innerHTML = `
              <div style="display:flex; align-items:center; gap:8px;">
                <img class="thumb" src="${escapeHtml(backupUrl)}" onerror="this.style.display='none';">
                <a href="${escapeHtml(backupUrl)}" target="_blank" style="font-size:12px; color:#2563eb; text-decoration:none; font-weight:600;">View ↗</a>
              </div>
            `;
          }
        }
      }
      updateStats();

    } catch (e: any) {
      console.error('Backup failed for IID', iid, e);
      if (actionCell) actionCell.innerHTML = `<button class="btn-backup" data-iid="${iid}" data-photo="${escapeHtml(photoUrl)}">Retry ↺</button>`;
      const retryBtn = actionCell?.querySelector('.btn-backup') as HTMLButtonElement;
      if (retryBtn) retryBtn.onclick = () => backupOne(iid, photoUrl, retryBtn);
      globalLog.textContent = `❌ Error IID ${iid}: ${e.message}`;
    }
  }

  // ── Backup All Pending using worker queue (Concurrency limit: 10) ───
  backupAllBtn.onclick = async () => {
    const pending = allRows.filter(r => !r.image_backup_url && r.student_photo_url);
    if (pending.length === 0) { globalLog.textContent = 'No pending records.'; return; }

    backupAllBtn.disabled = true;
    globalLog.textContent = `Starting backup of ${pending.length} records...`;

    const limit = 10;
    let index = 0;
    let completedCount = 0;
    let activeCount = 0;

    const worker = async (): Promise<void> => {
      while (index < pending.length) {
        const currentIndex = index++;
        if (currentIndex >= pending.length) break;

        const row = pending[currentIndex];
        activeCount++;
        globalLog.textContent = `Processing image ${completedCount + activeCount}/${pending.length} (Active uploads: ${activeCount})...`;

        try {
          await backupOne(String(row.iid), row.student_photo_url);
        } catch (err) {
          console.error(`Error backup queue for IID ${row.iid}:`, err);
        }

        activeCount--;
        completedCount++;
        globalLog.textContent = `Processed ${completedCount}/${pending.length} images (Active uploads: ${activeCount})...`;
      }
    };

    // Spawn initial workers
    const workers = [];
    for (let i = 0; i < Math.min(limit, pending.length); i++) {
      workers.push(worker());
    }

    await Promise.all(workers);

    globalLog.textContent = `✅ Backup complete! ${completedCount} images processed.`;
    backupAllBtn.disabled = false;
  };

  globalLog.textContent = '⚡ TypeScript Direct Upload (No PHP dependency) ✅';
  globalLog.style.color = '#0ea5e9';

  await loadData();
}
